// DispatchDeck — reminder time math (TASK-1550)
// ============================================================================
// `window.ReminderTime` — converts between an INSTANT (epoch ms, what the
// server stores as reminders.dueAt) and the COMPANY's wall clock
// (tenant `settings.timezone`, default America/Chicago). A dispatcher in
// Arizona setting "Sunday 9 PM" for a Central company means 9 PM Central.
//
// Pure, no imports, no DOM: loaded by reminders.js as a side-effect import and
// unit-tested in node (tests/task1550-reminders.unit.cjs runs this file in a vm
// with a stub `window`). Intl does all zone work, so DST follows the tz database
// rather than any hand-kept offset table.
//
// DST policy (pinned by the unit test):
//   • a wall time that does not exist (spring-forward gap, e.g. 2:30 AM on the
//     March change day in Central) moves FORWARD to the first real minute after
//     the gap (3:30 AM CDT) — the reminder is never dropped and never early.
//   • a wall time that happens twice (fall-back, 1:30 AM on the November change
//     day) resolves to the FIRST occurrence (1:30 AM CDT) — earlier is safer
//     for a reminder than an hour late.
// ============================================================================
(function (root) {
  'use strict';

  const DEFAULT_TZ = 'America/Chicago';
  const DAY_MS = 864e5;

  const _zoneOk = new Map();
  function resolveZone(tz) {
    const z = String(tz == null ? '' : tz).trim() || DEFAULT_TZ;
    if (_zoneOk.has(z)) return _zoneOk.get(z);
    let ok = DEFAULT_TZ;
    try { new Intl.DateTimeFormat('en-US', { timeZone: z }).format(new Date(0)); ok = z; } catch (e) { ok = DEFAULT_TZ; }
    if (_zoneOk.size < 64) _zoneOk.set(z, ok);
    return ok;
  }

  const _fmt = new Map();
  function partsFormatter(tz) {
    if (!_fmt.has(tz)) {
      _fmt.set(tz, new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', weekday: 'short',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }));
    }
    return _fmt.get(tz);
  }

  /** Wall-clock parts of an instant in a zone. month is 1-12. */
  function zonedParts(ms, tz) {
    const zone = resolveZone(tz);
    const out = {};
    for (const p of partsFormatter(zone).formatToParts(new Date(ms))) {
      if (p.type !== 'literal') out[p.type] = p.value;
    }
    const hour = Number(out.hour) % 24; // some engines print 24 for midnight
    return {
      year: Number(out.year), month: Number(out.month), day: Number(out.day),
      hour, minute: Number(out.minute), second: Number(out.second), weekday: out.weekday,
    };
  }

  /** Zone offset (ms, local minus UTC) in effect at an instant. */
  function offsetMs(ms, tz) {
    const base = Math.floor(ms / 1000) * 1000;
    const p = zonedParts(base, tz);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - base;
  }

  function _matches(ms, tz, y, mo, d, h, mi) {
    const p = zonedParts(ms, tz);
    return p.year === y && p.month === mo && p.day === d && p.hour === h && p.minute === mi;
  }

  /** Company wall time -> instant (ms). See the DST policy above. */
  function wallToUtcMs(y, mo, d, h, mi, tz) {
    const zone = resolveZone(tz);
    const guess = Date.UTC(y, mo - 1, d, h, mi, 0);
    if (!Number.isFinite(guess)) return NaN;
    // The offsets on either side of this date. Two candidates cover every case
    // as long as a zone changes offset at most once in 48h (true for all tzdb
    // zones in practice).
    const cA = guess - offsetMs(guess - DAY_MS, zone);
    const cB = guess - offsetMs(guess + DAY_MS, zone);
    const okA = _matches(cA, zone, y, mo, d, h, mi);
    const okB = _matches(cB, zone, y, mo, d, h, mi);
    if (okA && okB) return Math.min(cA, cB);   // ambiguous -> first occurrence
    if (okA) return cA;
    if (okB) return cB;
    return Math.max(cA, cB);                    // nonexistent -> after the gap
  }

  /** 'YYYY-MM-DD' + 'HH:MM' in company time -> ms (NaN when unparseable). */
  function fromInputs(dateStr, timeStr, tz) {
    const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || ''));
    const tm = /^(\d{1,2}):(\d{2})/.exec(String(timeStr || ''));
    if (!dm || !tm) return NaN;
    const h = Number(tm[1]);
    const mi = Number(tm[2]);
    if (h > 23 || mi > 59) return NaN;
    return wallToUtcMs(Number(dm[1]), Number(dm[2]), Number(dm[3]), h, mi, tz);
  }

  const pad = (n) => String(n).padStart(2, '0');

  /** ms -> { date:'YYYY-MM-DD', time:'HH:MM' } in company time (form fields). */
  function toInputs(ms, tz) {
    const p = zonedParts(ms, tz);
    return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
  }

  function _dayNumber(p) { return Math.round(Date.UTC(p.year, p.month - 1, p.day) / DAY_MS); }

  function _clock(p) {
    const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
    return `${h12}:${pad(p.minute)} ${p.hour < 12 ? 'AM' : 'PM'}`;
  }

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** "Today 9:00 PM" / "Tomorrow 8:00 AM" / "Sun, Oct 4, 9:00 PM" in company time. */
  function formatDue(ms, tz, nowMs) {
    if (!Number.isFinite(ms)) return '';
    const p = zonedParts(ms, tz);
    const n = zonedParts(Number.isFinite(nowMs) ? nowMs : Date.now(), tz);
    const diff = _dayNumber(p) - _dayNumber(n);
    const clock = _clock(p);
    if (diff === 0) return `Today ${clock}`;
    if (diff === 1) return `Tomorrow ${clock}`;
    if (diff === -1) return `Yesterday ${clock}`;
    const yr = p.year !== n.year ? `, ${p.year}` : '';
    return `${p.weekday}, ${MONTHS[p.month - 1]} ${p.day}${yr}, ${clock}`;
  }

  /** Short zone name at an instant, e.g. "CDT". */
  function zoneLabel(tz, ms) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: resolveZone(tz), timeZoneName: 'short' })
        .formatToParts(new Date(Number.isFinite(ms) ? ms : Date.now()));
      const z = parts.find((x) => x.type === 'timeZoneName');
      return z ? z.value : resolveZone(tz);
    } catch (e) { return resolveZone(tz); }
  }

  /** Company-day wall time `addDays` from now's company date at h:mi. */
  function _dayAt(nowMs, tz, addDays, h, mi) {
    const n = zonedParts(nowMs, tz);
    const d = new Date(Date.UTC(n.year, n.month - 1, n.day + addDays));
    return wallToUtcMs(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), h, mi, tz);
  }

  /**
   * Quick picks. Returns ms, or null when the pick makes no sense now
   * ("Tonight 8 PM" after 8 PM).
   *   in1h / in3h   — now + 1h / 3h, rounded UP to the next 5 minutes
   *   tonight       — today 8:00 PM company time
   *   tomorrow      — tomorrow 8:00 AM company time
   */
  function quickPick(kind, nowMs, tz) {
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();
    const round5 = (ms) => Math.ceil(ms / 300000) * 300000;
    if (kind === 'in1h') return round5(now + 3600000);
    if (kind === 'in3h') return round5(now + 3 * 3600000);
    if (kind === 'tonight') {
      const t = _dayAt(now, tz, 0, 20, 0);
      return t > now + 5 * 60000 ? t : null;
    }
    if (kind === 'tomorrow') return _dayAt(now, tz, 1, 8, 0);
    return null;
  }

  root.ReminderTime = {
    DEFAULT_TZ, resolveZone, zonedParts, offsetMs, wallToUtcMs,
    fromInputs, toInputs, formatDue, zoneLabel, quickPick,
  };
})(typeof window !== 'undefined' ? window : globalThis);
